//
// Copyright (c) 2021 Astute Global, Inc.
//

import i18next from "i18n/i18n";
import {
  addListToDropdown,
  ButtonView,
  Collection,
  createLabeledDropdown,
  CssTransitionDisablerMixin,
  IconCancel,
  IconCheck,
  LabeledFieldView,
  submitHandler,
  View,
  ViewModel,
} from "ckeditor5";
import { removeCkReset } from "../../ckEditorUtil";
import ConversationVariablesListView from "./conversationVariablesListView";
import { DEFAULT_INSERT_TYPE, INSERT_TYPES } from "../conversationVariableCommand";

import "../css/conversationVariableView.css";

class BaseConversationVariableView extends View {
  constructor(editor, command) {
    super(editor.locale);
    this.editor = editor;
    this.variableListView = this._createVariableListView(command);
    this.insertTypeSelectView = this._createInsertTypeSelectView();
    this.okButton = this._createOkButton();
    this.cancelButton = this._createCancelButton();
    this.set("insertType", command.insertType);
    this.bind("insertType").to(command);
    this.setTemplate({
      tag: "form",
      attributes: {
        class: ["ck", "ck-responsive-form", "ck-vertical-form", "conversation-variable-view"],
        tabindex: "-1",
      },
      children: this._createFormChildren(),
    });
  }

  get variableId() {
    if (this.variableListView) {
      return this.variableListView.variableId;
    }
    return null;
  }

  get variableName() {
    if (this.variableListView) {
      return this.variableListView.variableName;
    }
    return null;
  }

  focus() {
    this.variableListView.focus();
  }

  render() {
    super.render();
    // Remove the CKEditor reset classes, to better integrate with Marionette UI
    setTimeout(() => removeCkReset(this.element), 100);
    submitHandler({
      view: this,
    });
  }

  _createCancelButton() {
    const button = new ButtonView(this.locale);
    button.set({
      label: i18next.t("common.cancel"),
      icon: IconCancel,
      tooltip: true,
    });
    button.extendTemplate({
      attributes: {
        class: "ck-button-cancel",
      },
    });
    button.delegate("execute").to(this, "cancel");
    return button;
  }

  _createFormChildren() {
    const children = this.createCollection();

    children.add(this.variableListView);
    children.add(this.insertTypeSelectView);
    children.add(this.okButton);
    children.add(this.cancelButton);

    return children;
  }

  _createInsertTypes() {
    const insertTypes = new Collection();
    for (const insertType of INSERT_TYPES) {
      const model = new ViewModel({
        label: i18next.t(`ckeditor.conversationVariable.type.${insertType}`),
        insertType: insertType,
        isToggleable: true,
        tooltip: true,
        withText: true,
      });
      model.bind("isOn").to(this, "insertType", (value) => value == insertType);
      insertTypes.add({
        type: "button",
        model: model,
      });
    }
    return insertTypes;
  }

  _createInsertTypeSelectView() {
    const insertTypes = this._createInsertTypes();
    const insertTypeSelectView = new LabeledFieldView(this.locale, createLabeledDropdown);
    insertTypeSelectView.set({
      class: "insert-type-wrapper",
      isEmpty: false,
      label: i18next.t("ckeditor.conversationVariable.typeLabel"),
    });
    const insertTypeSelect = insertTypeSelectView.fieldView;
    addListToDropdown(insertTypeSelect, insertTypes);
    insertTypeSelect.class = "insert-type";
    insertTypeSelect.buttonView.set({
      label: i18next.t(`ckeditor.conversationVariable.type.${this.insertType}`),
      withText: true,
    });
    insertTypeSelect.buttonView.bind("label").to(this, "insertType", (value) => {
      for (const insertType of insertTypes) {
        if (value == insertType.model.insertType) {
          return insertType.model.label;
        }
      }
      return i18next.t(`ckeditor.conversationVariable.type.${DEFAULT_INSERT_TYPE}`);
    });
    insertTypeSelect.on("execute", (evt) => {
      this.insertType = evt.source.insertType;
    });
    return insertTypeSelectView;
  }

  _createOkButton() {
    const button = new ButtonView(this.locale);
    button.set({
      label: i18next.t("common.ok"),
      icon: IconCheck,
      tooltip: true,
    });
    button.extendTemplate({
      attributes: {
        class: "ck-button-save",
      },
    });
    button.type = "submit";
    button.delegate("execute").to(this, "submit");
    return button;
  }

  _createVariableListView(command) {
    const view = new ConversationVariablesListView(this.locale);
    view.variableId = command.value;
    view.bind("variableId").to(command, "value");
    return view;
  }
}

const ConversationVariableView = CssTransitionDisablerMixin(BaseConversationVariableView);

export default ConversationVariableView;
